import { AlertTriangle, Info, Pencil, Pin, Sparkles, Trash2, Wrench } from 'lucide-react';
import {
  ANNOUNCEMENT_KIND,
  ANNOUNCEMENT_KIND_LABEL,
  type Announcement,
  type AnnouncementKind,
} from '@electrodavisan/shared';
import { Badge, Button, Card, type BadgeTone } from '../../components/ui';

/**
 * Como se pinta cada clase de anuncio.
 *
 * El color y el icono se deciden AQUI y no en la tarjeta, para que un aviso
 * de mantenimiento se vea igual en el canal, en el panel y donde sea que
 * aparezca manana. Si estuviera repartido, en dos meses habria tres rojos
 * distintos.
 */
const ESTILO: Record<AnnouncementKind, { tono: BadgeTone; Icono: typeof Info }> = {
  [ANNOUNCEMENT_KIND.INFO]: { tono: 'neutral', Icono: Info },
  [ANNOUNCEMENT_KIND.UPDATE]: { tono: 'success', Icono: Sparkles },
  [ANNOUNCEMENT_KIND.MAINTENANCE]: { tono: 'warning', Icono: Wrench },
  [ANNOUNCEMENT_KIND.ALERT]: { tono: 'danger', Icono: AlertTriangle },
};

/**
 * Cuando salio, en palabras.
 *
 * «Hace 2 horas» se entiende sin pensar; «21/09/2026 08:14» obliga a calcular.
 * Pasada una semana se invierte: ahi lo util vuelve a ser la fecha, porque
 * «hace 23 dias» ya no le dice nada a nadie.
 */
function cuando(iso: string | null): string {
  if (!iso) return 'Sin publicar';

  const fecha = new Date(iso);
  const minutos = Math.round((Date.now() - fecha.getTime()) / 60_000);

  if (minutos < 1) return 'Ahora mismo';
  if (minutos < 60) return `Hace ${minutos} min`;

  const horas = Math.round(minutos / 60);
  if (horas < 24) return `Hace ${horas} h`;

  const dias = Math.round(horas / 24);
  if (dias === 1) return 'Ayer';
  if (dias < 7) return `Hace ${dias} dias`;

  return fecha.toLocaleDateString('es-CO', { day: 'numeric', month: 'long', year: 'numeric' });
}

export function AnnouncementCard({
  announcement,
  onEdit,
  onDelete,
}: {
  announcement: Announcement;
  /** Solo llegan con permiso de administracion; sin ellos no se pinta nada. */
  onEdit?: (announcement: Announcement) => void;
  onDelete?: (announcement: Announcement) => void;
}): JSX.Element {
  const { tono, Icono } = ESTILO[announcement.kind];
  const esBorrador = announcement.publishedAt === null;

  return (
    <Card className="p-5">
      <div className="flex items-start gap-3">
        <span
          aria-hidden
          className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary-soft text-primary-bright"
        >
          <Icono className="h-[18px] w-[18px]" />
        </span>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-[15px] font-semibold text-text-primary">{announcement.title}</h3>
            {announcement.pinned ? (
              <Pin className="h-3.5 w-3.5 text-primary-bright" aria-label="Fijado" />
            ) : null}
            <Badge tone={tono}>{ANNOUNCEMENT_KIND_LABEL[announcement.kind]}</Badge>
            {esBorrador ? <Badge tone="warning">Borrador</Badge> : null}
          </div>

          <p className="mt-1 text-[12.5px] text-text-muted">
            {cuando(announcement.publishedAt)}
            {announcement.authorName ? ` · ${announcement.authorName}` : ''}
          </p>

          {/*
            `whitespace-pre-line` y no un renderizador de Markdown.

            Quien escribe un anuncio lo escribe en una caja de texto, y lo que
            espera es que sus saltos de linea se respeten. Meter Markdown
            obligaria a aprender una sintaxis para algo que se redacta en dos
            minutos, y abriria la puerta a inyectar HTML en una pagina que ve
            toda la empresa.
          */}
          <p className="mt-3 whitespace-pre-line text-[13.5px] leading-relaxed text-text-secondary">
            {announcement.body}
          </p>

          {onEdit || onDelete ? (
            <div className="mt-4 flex gap-2">
              {onEdit ? (
                <Button
                  size="sm"
                  variant="secondary"
                  leftIcon={<Pencil className="h-3.5 w-3.5" aria-hidden />}
                  onClick={() => onEdit(announcement)}
                >
                  Editar
                </Button>
              ) : null}
              {onDelete ? (
                <Button
                  size="sm"
                  variant="secondary"
                  leftIcon={<Trash2 className="h-3.5 w-3.5" aria-hidden />}
                  onClick={() => onDelete(announcement)}
                >
                  Borrar
                </Button>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    </Card>
  );
}
